import React, { useState, useEffect, useCallback, useRef } from 'react';
import { cn } from '@rocco/utils/cn';
import { useProjectImages } from '../../hook/use-project-images';
import DetailBannerSection from '~/modules/page-builder/component/detail/detail-banner-section/DetailBannerSection';
import { getImagesSizes } from '~/utils/image-sizes-utils';
import {
    BANNER_WIDTH_FULL_MOBILE,
    FULL_COLUMN_WIDTH_FULL_SCREEN,
} from '~/modules/page-builder/config/column-constants';
import DownloadAction from '~/modules/page-builder/component/detail/detail-banner-section/actions/DownloadAction';
import { getBannerDimensions } from '~/utils/banner-utils';
import type { ProjectPageLoader } from '@modules/project/type/loader';
import { RoccoLink } from '@archipro/rocco/components/link/RoccoLink';
import ProjectBannerLogoIcon from './ProjectBannerLogoIcon';
import { useUser } from '~/modules/user';
import { useFeature } from '~/modules/root';

interface ProjectBannerCarouselProps {
    canDownloadBannerImage: boolean;
    project: ProjectPageLoader['project'];
}

/**
 * Preloads images to ensure they're ready when needed
 */
const preloadImage = (src: string): Promise<void> => {
    return new Promise((resolve, reject) => {
        const img = new Image();
        img.onload = () => resolve();
        img.onerror = () => reject();
        img.src = src;
    });
};

/**
 * YouTube video background component - the existing banner component doesn't
 * autoplay youtube videos, this component will extract the video id from the url
 * and return the embedded link to allow for autoplaying the video on the banner.
 */
const YouTubeVideoBackground = ({ videoUrl }: { videoUrl: string }) => {
    // Extract YouTube video ID from URL
    const getYouTubeVideoId = (url: string) => {
        const regExp =
            /^.*(youtu.be\/|v\/|u\/\w\/|embed\/|watch\?v=|&v=)([^#&?]*).*/;
        const match = url.match(regExp);
        return match && match[2] && match[2].length === 11 ? match[2] : null;
    };

    const videoId = getYouTubeVideoId(videoUrl);

    if (!videoId || !videoUrl) {
        return null;
    }

    return (
        <div className="top:0 absolute inset-0 overflow-hidden bg-black">
            <iframe
                src={`https://www.youtube.com/embed/${videoId}?autoplay=1&mute=1&controls=0&showinfo=0&rel=0&modestbranding=1&playsinline=1`}
                className="object-cover"
                style={{
                    width: '100vw',
                    height: 'calc((100vw * 9) / 16)',
                    border: 'none',
                }}
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                allowFullScreen
                title="YouTube video player"
            />
        </div>
    );
};

/**
 * Carousel component that rotates through project images every 5 seconds
 */
const ProjectBannerCarousel = ({
    canDownloadBannerImage,
    project,
}: ProjectBannerCarouselProps) => {
    const { allImages: moduleImages } = useProjectImages();

    // Temporarily disable carousel functionality
    const [currentImageIndex, setCurrentImageIndex] = useState(0);
    const [isAutoplayPaused, setIsAutoplayPaused] = useState(true); // Disable autoplay from start
    const [autoplayTimeout, setAutoplayTimeout] =
        useState<NodeJS.Timeout | null>(null);
    const [loadedImages, setLoadedImages] = useState<Set<string>>(new Set());

    // Drag functionality states
    const [isDragging, setIsDragging] = useState(false);
    const [dragStartX, setDragStartX] = useState(0);
    const [dragDistance, setDragDistance] = useState(0);
    const bannerRef = useRef<HTMLDivElement>(null);

    const user = useUser();
    const isAPStaff = user.__typename === 'Me' && user.IsArchiproStaff;

    const bannerSize = getImagesSizes({
        desktop: FULL_COLUMN_WIDTH_FULL_SCREEN,
        mobile: BANNER_WIDTH_FULL_MOBILE,
    });
    const bannerDimensions = getBannerDimensions('project');

    const isAiHeroSummarizationEnabled = useFeature(
        'project_ai_hero_summarization'
    );

    // Combine banner image with module images
    const allImages = React.useMemo(() => {
        const imageArray = [];

        // Add banner image first if it exists
        if (project.Banner?.imageUrl) {
            imageArray.push({
                id: 'banner',
                imageUrl: project.Banner.imageUrl,
                videoUrl: project.Banner.videoBannerUrl,
                caption: project.Banner.caption,
                libraryImage: project.Banner.libraryImage,
                downloadLink: project.Banner.downloadLink,
                isBanner: true,
                isPortrait: false, // Banner images are typically landscape
            });
        }

        // Add module images and videos in their original fetched order
        moduleImages.forEach(
            (image: {
                id: string;
                imageUrl: string;
                videoUrl?: string;
                caption?: string;
                libraryImage?: { ID?: number };
                downloadLink?: string;
                isPortrait?: boolean;
            }) => {
                imageArray.push({
                    ...image,
                    isBanner: false,
                });
            }
        );

        return imageArray;
    }, [project.Banner, moduleImages]);

    // Preload all images when component mounts
    useEffect(() => {
        if (allImages.length === 0) return;

        const preloadAllImages = async () => {
            try {
                await Promise.allSettled(
                    allImages.map(async (img) => {
                        await preloadImage(img.imageUrl);
                        setLoadedImages((prev) =>
                            new Set(prev).add(img.imageUrl)
                        );
                    })
                );
            } catch (error) {
                console.warn('Some images failed to preload:', error);
            }
        };

        preloadAllImages();
    }, [allImages]);

    // Function to pause autoplay and set up resume timeout
    const pauseAutoplay = useCallback(() => {
        setIsAutoplayPaused(true);

        // Clear any existing timeout
        if (autoplayTimeout) {
            clearTimeout(autoplayTimeout);
        }

        // Set new timeout to resume autoplay after 3 seconds
        const timeout = setTimeout(() => {
            setIsAutoplayPaused(false);
        }, 3000);

        setAutoplayTimeout(timeout);
    }, [autoplayTimeout]);

    // Cleanup timeout on unmount
    useEffect(() => {
        return () => {
            if (autoplayTimeout) {
                clearTimeout(autoplayTimeout);
            }
        };
    }, [autoplayTimeout]);

    // Auto-rotate carousel every 5 seconds, pausing when user interacts with the preview strip
    useEffect(() => {
        if (allImages.length <= 1 || isAutoplayPaused || isDragging) {
            return;
        }

        // Pause autoplay if current image has a video
        const currentImage = allImages[currentImageIndex];
        if (currentImage?.videoUrl) {
            return;
        }

        const interval = setInterval(() => {
            setCurrentImageIndex(
                (prevIndex) => (prevIndex + 1) % allImages.length
            );
        }, 5000);

        return () => clearInterval(interval);
    }, [
        allImages.length,
        isAutoplayPaused,
        currentImageIndex,
        allImages,
        isDragging,
    ]);

    const [isHovered, setIsHovered] = useState(false);

    useEffect(() => {
        const handleMouseEnter = () => {
            setIsHovered(true);
        };
        const handleMouseLeave = () => {
            setIsHovered(false);
        };

        const bannerElement = bannerRef.current;
        if (bannerElement) {
            bannerElement.addEventListener('mouseenter', handleMouseEnter);
            bannerElement.addEventListener('mouseleave', handleMouseLeave);
        }

        return () => {
            if (bannerElement) {
                bannerElement.removeEventListener(
                    'mouseenter',
                    handleMouseEnter
                );
                bannerElement.removeEventListener(
                    'mouseleave',
                    handleMouseLeave
                );
            }
        };
    }, []);

    // Manual navigation without transition effect
    const goToImage = useCallback(
        (index: number) => {
            if (index === currentImageIndex || allImages.length <= 1) return;
            pauseAutoplay();
            setCurrentImageIndex(index);
        },
        [currentImageIndex, allImages.length, pauseAutoplay]
    );

    // Navigate to next/previous image
    const goToNextImage = useCallback(() => {
        if (allImages.length <= 1) return;
        const nextIndex = (currentImageIndex + 1) % allImages.length;
        goToImage(nextIndex);
    }, [currentImageIndex, allImages.length, goToImage]);

    const goToPreviousImage = useCallback(() => {
        if (allImages.length <= 1) return;
        const prevIndex =
            currentImageIndex === 0
                ? allImages.length - 1
                : currentImageIndex - 1;
        goToImage(prevIndex);
    }, [currentImageIndex, allImages.length, goToImage]);

    // Drag functionality for banner area
    const handleDragStart = useCallback(
        (e: React.MouseEvent | React.TouchEvent) => {
            if (allImages.length <= 1) return;

            setIsDragging(true);
            pauseAutoplay();

            const clientX =
                'touches' in e ? (e.touches[0]?.clientX ?? 0) : e.clientX;
            setDragStartX(clientX);
            setDragDistance(0);
        },
        [allImages.length, pauseAutoplay]
    );

    const handleDragMove = useCallback(
        (e: MouseEvent | TouchEvent) => {
            if (!isDragging) return;

            const clientX =
                'touches' in e ? (e.touches[0]?.clientX ?? 0) : e.clientX;
            const distance = clientX - dragStartX;
            setDragDistance(distance);
        },
        [isDragging, dragStartX]
    );

    const handleDragEnd = useCallback(() => {
        if (!isDragging) return;

        setIsDragging(false);

        // Determine if drag distance is enough to trigger navigation
        const threshold = 50; // minimum drag distance to trigger navigation

        if (Math.abs(dragDistance) > threshold) {
            if (dragDistance > 0) {
                goToPreviousImage();
            } else {
                goToNextImage();
            }
        }

        setDragDistance(0);
    }, [isDragging, dragDistance, goToPreviousImage, goToNextImage]);

    // Add/remove global event listeners for drag
    useEffect(() => {
        if (isDragging) {
            document.addEventListener('mousemove', handleDragMove);
            document.addEventListener('mouseup', handleDragEnd);
            document.addEventListener('touchmove', handleDragMove);
            document.addEventListener('touchend', handleDragEnd);

            return () => {
                document.removeEventListener('mousemove', handleDragMove);
                document.removeEventListener('mouseup', handleDragEnd);
                document.removeEventListener('touchmove', handleDragMove);
                document.removeEventListener('touchend', handleDragEnd);
            };
        }
    }, [isDragging, handleDragMove, handleDragEnd]);

    if (allImages.length === 0) {
        return null;
    }

    const currentImage = allImages[currentImageIndex];

    // Safety check for currentImage
    if (!currentImage) {
        return null;
    }

    const disableDrag = true;

    return (
        <div className="w-full">
            <div
                ref={bannerRef}
                className={cn('relative select-none', isDragging && '')}
                onMouseDown={disableDrag ? undefined : handleDragStart}
                onTouchStart={disableDrag ? undefined : handleDragStart}
            >
                {/* For images and non youtube videos, we use existing details banner section */}
                {!currentImage.isPortrait ? (
                    <div className="group relative w-full overflow-hidden bg-black md:aspect-[28/17]">
                        <DetailBannerSection
                            {...project.Banner}
                            imageUrl={currentImage.imageUrl}
                            title={project.Title}
                            dimensions={bannerDimensions}
                            size={bannerSize}
                            isVideoBanner={
                                !!currentImage.videoUrl &&
                                !(
                                    currentImage.videoUrl.includes('youtube') ||
                                    currentImage.videoUrl.includes('youtu.be')
                                )
                            }
                            videoBannerUrl={currentImage.videoUrl ?? ''}
                            showBackButton={false}
                            awards={project.Awards}
                            actions={
                                //only show download action if user is AP staff and Desktop version
                                isAPStaff
                                    ? [
                                          <DownloadAction
                                              key="downloadAction"
                                              canDownload={
                                                  canDownloadBannerImage
                                              }
                                              downloadLink={
                                                  project.Banner.downloadLink ??
                                                  ''
                                              }
                                              libraryImageID={
                                                  project.Banner.libraryImage
                                                      ?.ID ?? 0
                                              }
                                          />,
                                      ]
                                    : []
                            }
                        />

                        <div
                            className={cn(
                                'absolute bottom-12 right-12 z-50 flex min-h-8 min-w-8'
                            )}
                            style={{
                                opacity: isHovered ? 1 : 0,
                                transition: 'opacity 0.1s ease-in-out',
                            }}
                        >
                            {isAPStaff && (
                                <DownloadAction
                                    key="downloadAction"
                                    canDownload={canDownloadBannerImage}
                                    downloadLink={
                                        project.Banner.downloadLink ?? ''
                                    }
                                    libraryImageID={
                                        project.Banner.libraryImage?.ID ?? 0
                                    }
                                />
                            )}
                        </div>
                    </div>
                ) : (
                    /* Custom portrait image display to match design */
                    <div className="relative aspect-[1/1] w-full overflow-hidden bg-black md:aspect-[28/17]">
                        {/* Loading indicator for portrait images */}
                        {!loadedImages.has(currentImage.imageUrl) && (
                            <div className="absolute inset-0 z-30 flex items-center justify-center bg-black">
                                <div className="h-12 w-12 animate-spin rounded-full border-4 border-white border-t-transparent"></div>
                            </div>
                        )}
                        <div className="flex h-full w-full items-center justify-center">
                            <img
                                src={currentImage.imageUrl}
                                alt={currentImage.caption || 'Portrait image'}
                                className="max-h-full w-auto object-contain"
                                onLoad={() =>
                                    setLoadedImages((prev) =>
                                        new Set(prev).add(currentImage.imageUrl)
                                    )
                                }
                            />
                        </div>
                        {/* Download action for portrait images */}
                        {canDownloadBannerImage && currentImage.isBanner && (
                            <div className="absolute right-4 top-4 z-20">
                                <DownloadAction
                                    canDownload={true}
                                    downloadLink={
                                        currentImage.downloadLink ?? ''
                                    }
                                    libraryImageID={
                                        currentImage.libraryImage?.ID ?? 0
                                    }
                                />
                            </div>
                        )}
                    </div>
                )}

                {/* YouTube video background for YouTube URLs */}
                {currentImage.videoUrl &&
                    (currentImage.videoUrl.includes('youtube') ||
                        currentImage.videoUrl.includes('youtu.be')) && (
                        <YouTubeVideoBackground
                            videoUrl={currentImage.videoUrl}
                        />
                    )}

                {/* Overlay */}
                {/* <div className="absolute inset-0 bg-black/40" /> */}

                {/* Content */}
                <div className="absolute left-0 top-0 z-10 grid h-full w-full grid-cols-2 items-center px-12">
                    <div className="flex -translate-x-[45%] rotate-90 items-center justify-start justify-self-start">
                        <div className="grid origin-center grid-cols-[auto_auto] items-center gap-4">
                            <ProjectBannerLogoIcon className="h-12 w-12 -rotate-90" />
                            <RoccoLink
                                to={project.Professional?.Link ?? '#'}
                                className="text-base font-bold uppercase tracking-[3px] text-white"
                                style={{ fontFamily: 'ArchiProDisplay' }}
                            >
                                {project.Professional?.Title}
                            </RoccoLink>
                        </div>
                    </div>
                    <div className="flex w-full items-center justify-end">
                        <h1
                            className="my-0 break-words text-right text-[8.5rem] font-medium tracking-[-0.19625rem] text-white"
                            style={{
                                fontFamily: 'TWKGhost',
                                lineHeight: '88%',
                            }}
                        >
                            {/* Always use the hero title if it exists, otherwise use the project title */}
                            {isAiHeroSummarizationEnabled &&
                            project.ProjectHeroTitle
                                ? (project.ProjectHeroTitle ?? project.Title)
                                : project.Title}
                        </h1>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default ProjectBannerCarousel;
